import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notification } from 'tea-component';
import {
  batchUpdateStudiosNotes,
  getStudiosNotesList,
  type IStudiosNoteItem,
} from '@sg/services/studio';
import type { ITitleNote } from '@sg/pages/StudioManagement/InDevProjects/pages/InDevProjectsAll/component/EditNotesModal';

const parseAttach = (raw: any): ITitleNote['attach'] => {
  if (Array.isArray(raw)) {
    return raw.map((item: any) => ({
      name: item?.name || item?.file || '',
      path: item?.path || '',
    }));
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.map((item: any) => ({
          name: item?.name || item?.file || '',
          path: item?.path || '',
        }))
        : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeAttachForCompare = (attach: any) => parseAttach(attach)
  .map((f) => ({ name: f?.name || '', path: f?.path || '' }))
  .filter((f) => f.name || f.path)
  .sort((a, b) => `${a.name}-${a.path}`.localeCompare(`${b.name}-${b.path}`));

const mapStudioToTitleNote = (
  studio: IStudiosNoteItem,
  date: string,
): ITitleNote => ({
  project_id: studio.studio_id,
  game_title: studio.studio_name,
  avatar: studio.cover,
  note: studio.note || '',
  attach: parseAttach(studio.attach),
  date,
});

const resolveSaveDate = (apiDate: string, fallbackDate?: string): string => {
  if (apiDate) {
    return apiDate;
  }
  if (fallbackDate) {
    return fallbackDate;
  }
  return '';
};

export const useStudiosBatchNotes = (
  fallbackDate?: string,
  options?: { enabled?: boolean },
) => {
  const enabled = options?.enabled ?? true;
  const [notes, setNotes] = useState<ITitleNote[]>([]);
  const [noteDate, setNoteDate] = useState('');
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const initialNotesRef = useRef<ITitleNote[]>([]);

  const noteMonth = useMemo(
    () => (noteDate ? noteDate.slice(0, 7) : (fallbackDate ? fallbackDate.slice(0, 7) : '')),
    [noteDate, fallbackDate],
  );

  const fetchStudiosNotes = useCallback(async (): Promise<ITitleNote[]> => {
    setFetching(true);
    try {
      const payload = fallbackDate ? { date: fallbackDate } : {};
      const response = await getStudiosNotesList(payload);
      if (response?.code !== 0) {
        throw new Error(response?.msg || 'Failed to fetch studios notes');
      }

      const responseDate = response?.data?.date || '';
      const formatted = (response?.data?.studios || []).map((studio: IStudiosNoteItem) =>
        mapStudioToTitleNote(studio, responseDate),
      );

      setNoteDate(responseDate);
      setNotes(formatted);
      initialNotesRef.current = formatted;
      return formatted;
    } catch (error) {
      console.error('[Studios] Failed to fetch batch notes:', error);
      notification.error({
        description: 'Failed to load studios notes',
      });
      throw error;
    } finally {
      setFetching(false);
    }
  }, [fallbackDate]);

  // Preload batch notes on page load / month change so modal opens instantly.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    fetchStudiosNotes().catch(() => undefined);
  }, [enabled, fetchStudiosNotes]);

  const saveStudiosNotes = useCallback(async (): Promise<boolean> => {
    const saveDate = resolveSaveDate(noteDate, fallbackDate);
    if (!saveDate) {
      notification.error({
        description: 'Notes month is unavailable',
      });
      return false;
    }

    const baselineMap = new Map<string, { note: string; attach: ReturnType<typeof normalizeAttachForCompare> }>();
    (initialNotesRef.current || []).forEach((item) => {
      const key = String(item?.project_id || '');
      if (!key) {
        return;
      }
      baselineMap.set(key, {
        note: item?.note || '',
        attach: normalizeAttachForCompare(item?.attach),
      });
    });

    const changedNotes = (notes || [])
      .map((item) => {
        const studioId = String(item?.project_id || '');
        if (!studioId) {
          return null;
        }

        const currentAttach = normalizeAttachForCompare(item?.attach);
        const baseline = baselineMap.get(studioId);
        const changed = !baseline
          || baseline.note !== (item?.note || '')
          || JSON.stringify(baseline.attach) !== JSON.stringify(currentAttach);

        if (!changed) {
          return null;
        }

        return {
          studio_id: studioId,
          note: item?.note || '',
          attach: JSON.stringify(currentAttach),
        };
      })
      .filter(Boolean);

    if (!changedNotes.length) {
      return true;
    }

    setSaving(true);
    try {
      const response = await batchUpdateStudiosNotes({
        date: saveDate,
        notes: changedNotes as any[],
      });

      if (response?.code !== 0) {
        throw new Error(response?.msg || 'Failed to save studios notes');
      }

      const failed = response?.data?.failed || [];
      if (failed.length) {
        notification.error({
          description: failed
            .map((item: any) => `${item?.studio_name || item?.studio_id}: ${item?.message}`)
            .join('\n'),
        });
        await fetchStudiosNotes();
        // Partial failure: close modal after showing failed studio details.
        return true;
      }

      notification.success({
        description: 'Operate Successfully',
      });
      await fetchStudiosNotes();
      return true;
    } catch (error) {
      console.error('[Studios] Failed to save batch notes:', error);
      notification.error({
        description: 'Failed to save studios notes',
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [fallbackDate, fetchStudiosNotes, noteDate, notes]);

  return {
    notes,
    setNotes,
    noteMonth,
    fetching,
    saving,
    fetchStudiosNotes,
    saveStudiosNotes,
  };
};
